import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import Navigation from "./Navigation";
import PropTypes from "prop-types";
import { useAuth } from "../context/AuthContext.jsx";
import logger from '../utils/logger';

const OrderConfirmation = ({
  handleUpdateQuantity,
  handleRemoveItem,
  setCartItems,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    logger.info("OrderConfirmation: Component loaded, clearing cart");
    
    // Clear cart permanently when landing on order confirmation
    setCartItems([]);
    localStorage.removeItem("cartItems");
    logger.info("OrderConfirmation: Cart cleared on confirmation page load");
  }, [setCartItems]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation
        handleUpdateQuantity={handleUpdateQuantity}
        handleRemoveItem={handleRemoveItem}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 font-handwritten">
                Thank You for Your Order!
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                Your payment has been processed successfully.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                What happens next?
              </h2>
              <div className="space-y-3 text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 dark:text-blue-400 text-sm font-semibold">1</span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    You&apos;ll receive an order confirmation email shortly with all the details
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 dark:text-blue-400 text-sm font-semibold">2</span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Your order will be processed and prepared for shipping
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 dark:text-blue-400 text-sm font-semibold">3</span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    We&apos;ll send you tracking information once your order ships
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/shop")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Continue Shopping
              </Button>
              <Button
                onClick={() => navigate("/order-history")}
                variant="outline"
                className="px-6 py-2 rounded-lg transition-colors"
              >
                View My Orders
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

OrderConfirmation.propTypes = {
  handleUpdateQuantity: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
  setCartItems: PropTypes.func.isRequired,
};

export default OrderConfirmation;
